import {
  handleSubmitMultilingualForm,
  useCreateContact,
  useGetContactFormData,
  useUpdateContact,
} from "@/api/cms.ts";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { ContactForm } from "@/components/cms/ContactForm.tsx";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import {
  cmsPageLocaleBaseSchemaShape,
  contactContentSchema,
  withPageNameRequiredWhenPublished,
} from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  withPageNameRequiredWhenPublished(
    contactContentSchema.omit({ id: true }).merge(cmsPageLocaleBaseSchemaShape),
  ),
);

type FormData = z.infer<typeof formSchema>;

const ContactCMS = () => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();
  const { instanceData } = useAuth();

  const { data: contactData } = useGetContactFormData();
  const create = useCreateContact();
  const update = useUpdateContact();

  const defaultValues: FormData = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const contact = contactData?.[lang];
        return {
          ...contact?.content,
          published: contact?.published ?? false,
          pageName: contact?.pageName ?? "",
          ...pageThemeFromDto(contact),
        };
      }, websiteLanguages),
    [contactData, websiteLanguages],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { reset } = form;

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (
      !hasInitialized.current &&
      contactData != null &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [reset, defaultValues, contactData]);

  const { toast } = useToast();

  const onSubmit = (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = contactData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            content,
          }),
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("contactSuccessMessage"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("contactErrorMessage"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout pageName={t("cmsTabContact")} formIds={["contact"]}>
      <FormProvider {...form}>
        <FormFieldVisibilityProvider formKey="cms-contact">
        <CmsPageAppearanceConfig formId="contact" />
        <form
          onSubmit={handleSubmitMultilingualForm(form, contactData, onSubmit)}
          id="contact"
          className="flex w-full flex-col gap-6"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`${lang}.published`} />
            )}
            render={(lang) => <ContactForm language={lang} />}
          />
        </form>
              </FormFieldVisibilityProvider>
      </FormProvider>
    </CMSLayout>
  );
};

export default ContactCMS;
